import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

export const THEME_PREFERENCES = Object.freeze(['system', 'light', 'dark']);
export const THEME_STORAGE_KEY = 'geek-automation-system.theme';
export const THEME_COLORS = Object.freeze({ dark: '#070b12', light: '#f4f7fb' });

const ThemeContext = createContext(null);

export function normalizeTheme(value) {
  return THEME_PREFERENCES.includes(value) ? value : 'system';
}

export function resolveTheme(preference, prefersDark = false) {
  const normalized = normalizeTheme(preference);
  return normalized === 'system' ? (prefersDark ? 'dark' : 'light') : normalized;
}

function getMediaQuery() {
  try {
    return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia('(prefers-color-scheme: dark)')
      : null;
  } catch {
    return null;
  }
}

export function readThemePreference(storage) {
  try {
    const target = storage ?? window.localStorage;
    return normalizeTheme(target.getItem(THEME_STORAGE_KEY));
  } catch {
    return 'system';
  }
}

export function applyTheme(preference, options = {}) {
  const root = options.root ?? (typeof document !== 'undefined' ? document.documentElement : null);
  const media = options.media ?? getMediaQuery();
  const resolved = resolveTheme(preference, Boolean(media?.matches));
  if (!root) return resolved;

  root.dataset.theme = resolved;
  root.dataset.bsTheme = resolved;
  root.style.colorScheme = resolved;

  const meta = options.meta ?? (typeof document !== 'undefined'
    ? document.querySelector('meta[name="theme-color"]')
    : null);
  if (meta) meta.setAttribute('content', THEME_COLORS[resolved]);
  return resolved;
}

export function ThemeProvider({ children }) {
  const [preference, setPreferenceState] = useState(() => readThemePreference());
  const [resolvedTheme, setResolvedTheme] = useState(
    () => resolveTheme(preference, Boolean(getMediaQuery()?.matches)),
  );

  const setPreference = useCallback((nextPreference) => {
    const normalized = normalizeTheme(nextPreference);
    try { window.localStorage.setItem(THEME_STORAGE_KEY, normalized); } catch {}
    setPreferenceState(normalized);
    setResolvedTheme(applyTheme(normalized));
  }, []);

  useEffect(() => {
    const media = getMediaQuery();
    const sync = () => setResolvedTheme(applyTheme(preference, { media }));
    sync();

    if (preference !== 'system' || !media) return undefined;
    if (typeof media.addEventListener === 'function') media.addEventListener('change', sync);
    else if (typeof media.addListener === 'function') media.addListener(sync);
    return () => {
      if (typeof media.removeEventListener === 'function') media.removeEventListener('change', sync);
      else if (typeof media.removeListener === 'function') media.removeListener(sync);
    };
  }, [preference]);

  useEffect(() => {
    const syncAcrossTabs = (event) => {
      if (event.key === THEME_STORAGE_KEY) setPreferenceState(normalizeTheme(event.newValue));
    };
    window.addEventListener('storage', syncAcrossTabs);
    return () => window.removeEventListener('storage', syncAcrossTabs);
  }, []);

  const value = useMemo(
    () => ({ preference, resolvedTheme, setPreference }),
    [preference, resolvedTheme, setPreference],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used inside ThemeProvider');
  return context;
}
